import {
  ArrowRight,
  BadgeCheck,
  BookOpenCheck,
  CalendarCheck,
  CheckCircle2,
  Dumbbell,
  FileText,
  Layers3,
  Sparkles,
  Trophy,
  Zap,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth-context.js'
import { useLearningPlan } from '../modules/learning/useLearningPlan.js'

function readLocalList(key) {
  if (typeof window === 'undefined') return []
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || '[]')
    return Array.isArray(parsed) ? parsed.filter(Boolean) : []
  } catch {
    return []
  }
}

function getTarget(plan) {
  return plan?.targetDirection || plan?.target_direction || plan?.direction || ''
}

function DashboardCard({
  icon: Icon,
  title,
  eyebrow,
  value,
  description,
  to,
  accent = 'blue',
  meta,
}) {
  return (
    <Link className={`student-dashboard-card accent-${accent}`} to={to}>
      <span className="student-dashboard-card-icon"><Icon size={20} /></span>
      <span className="student-dashboard-card-copy">
        <span>{eyebrow}</span>
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
      <span className="student-dashboard-card-side">
        <strong>{value}</strong>
        <small>{meta}</small>
        <ArrowRight size={17} />
      </span>
    </Link>
  )
}

function StudentDashboardPage() {
  const { user } = useAuth()
  const learning = useLearningPlan()
  const [localState, setLocalState] = useState({
    competitions: [],
    certificates: [],
    training: [],
  })

  useEffect(() => {
    setLocalState({
      competitions: readLocalList('student_plan_competitions'),
      certificates: readLocalList('student_plan_certificates'),
      training: readLocalList('student_plan_training'),
    })
  }, [])

  const activePlan = learning.plan
  const target = getTarget(activePlan)
  const roadmapSummary = learning.summary
  const currentStage = learning.currentStage
  const currentStep = learning.currentStep
  const xp = learning.xp
  const timeline = learning.timeline
  const isAiPlan = activePlan?.source === 'ai_generated' || Number(activePlan?.schemaVersion) >= 2
  const learnHref = currentStep?.id
    ? `${isAiPlan ? '/learning/ai/' : '/learning/'}${encodeURIComponent(currentStep.id)}`
    : currentStage ? '/learning' : '/onboarding'
  const competitionText = localState.competitions[0]?.title
    || localState.competitions[0]?.name
    || currentStage?.competitions?.[0]
    || '待推荐'
  const certificateText = localState.certificates[0]?.title
    || localState.certificates[0]?.name
    || currentStage?.certificates?.[0]
    || '待规划'
  const trainingText = localState.training[0]?.title
    || localState.training[0]?.name
    || currentStep?.exercise
    || '跟随今日学习生成'

  return (
    <main className="page-container student-dashboard-page learning-os-dashboard">
      <section className="student-dashboard-hero">
        <div>
          <span><Sparkles size={17} /> Learning OS</span>
          <h1>{user?.username ? `${user.username}，今天该做什么？` : '今天该做什么？'}</h1>
          <p>
            {target
              ? `当前目标：${target}。今天继续 ${currentStage?.name || '学习路线'}。`
              : '先完成 30 秒 Onboarding，系统会生成每天可执行的学习路线。'}
          </p>
        </div>
        <div className="student-dashboard-hero-actions">
          <Link className="primary-button" to={learnHref}>
            {target ? '立即学习' : '30 秒生成学习计划'} <ArrowRight size={16} />
          </Link>
          <Link className="secondary-button" to="/learning">
            查看路线
          </Link>
        </div>
      </section>

      {learning.error ? (
        <section className="student-dashboard-notice">
          <CheckCircle2 size={18} />
          <span>{learning.error}</span>
        </section>
      ) : null}

      <section className="learning-os-today-card">
        <div className="learning-os-today-copy">
          <span><Zap size={18} /> 今天该做什么</span>
          <h2>{currentStep?.title || '先生成你的学习计划'}</h2>
          <p>{currentStage ? `继续 ${currentStage.name}，完成后获得 ${currentStep?.xp || 20} XP，并推进路线。` : '完成 Onboarding 后，这里会显示第一天任务。'}</p>
        </div>
        <ol className="learning-os-today-list">
          <li><strong>1</strong><span>{currentStep ? `${currentStep.title}，${currentStep.minutes} 分钟` : '选择年级和专业'}</span></li>
          <li><strong>2</strong><span>{currentStep?.exercise || '生成一个练习'}</span></li>
          <li><strong>3</strong><span>{currentStep?.quiz || '完成一个小测试'}</span></li>
        </ol>
        <Link className="primary-button" to={learnHref}>
          立即学习 <ArrowRight size={16} />
        </Link>
      </section>

      <section className="student-dashboard-overview">
        <article>
          <span>学习进度</span>
          <strong>{roadmapSummary.progress || 0}%</strong>
          <p>{roadmapSummary.completedCount || 0}/{roadmapSummary.total || 0} 阶段</p>
        </article>
        <article>
          <span>当前知识点</span>
          <strong>{currentStep?.title || '待生成'}</strong>
          <p>{currentStage?.name || '先完成 Onboarding'}</p>
        </article>
        <article>
          <span>XP</span>
          <strong>{xp}</strong>
          <p>{timeline[0]?.title || '完成学习后自动记录时间线'}</p>
        </article>
      </section>

      <section className="student-dashboard-grid is-core-dashboard" aria-label="Learning OS 入口">
        <DashboardCard
          icon={CalendarCheck}
          title="今日学习"
          eyebrow="Today"
          value={currentStep ? `${currentStep.minutes}m` : '开始'}
          meta={currentStage?.name || 'Onboarding'}
          description={currentStep?.exercise || '每天只显示下一步。'}
          to={learnHref}
          accent="blue"
        />
        <DashboardCard
          icon={BookOpenCheck}
          title="学习路线"
          eyebrow="Roadmap"
          value={`${roadmapSummary.progress || 0}%`}
          meta={currentStage?.name || '待生成'}
          description="查看当前阶段和后续解锁路线。"
          to="/learning"
          accent="green"
        />
        <DashboardCard
          icon={Dumbbell}
          title="项目学习"
          eyebrow="Project"
          value="练习"
          meta={currentStep?.project || currentStage?.projects?.[0] || '待推荐'}
          description="从当前知识点进入推荐项目学习。"
          to={currentStep ? learnHref : '/projects'}
          accent="teal"
        />
        <DashboardCard
          icon={Trophy}
          title="竞赛推荐"
          eyebrow="Competition"
          value={localState.competitions.length || currentStage?.competitions?.length || '0'}
          meta={competitionText}
          description="跟随当前阶段出现，不再作为首页主入口。"
          to="/more/competitions"
          accent="orange"
        />
        <DashboardCard
          icon={BadgeCheck}
          title="证书规划"
          eyebrow="Certificate"
          value={localState.certificates.length || currentStage?.certificates?.length || '0'}
          meta={certificateText}
          description="只保留和当前路线相关的证书。"
          to="/more/certificates"
          accent="purple"
        />
        <DashboardCard
          icon={Layers3}
          title="实训任务"
          eyebrow="Training"
          value="1"
          meta={trainingText}
          description="把知识点拆成可交付练习。"
          to="/exercise"
          accent="slate"
        />
        <DashboardCard
          icon={FileText}
          title="简历状态"
          eyebrow="Resume"
          value="后置"
          meta="完成项目后"
          description="项目完成后再整理简历表达。"
          to="/more/resume"
          accent="slate"
        />
      </section>
    </main>
  )
}

export default StudentDashboardPage
